import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { entityOfType } from "../../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/**
 * Get AP Invoice Step
 *
 * Loads an existing AP invoice and re-exposes the fields downstream AP steps
 * need (the entity reference and the extracted referenceIds for Famous PO/SO/
 * TO lookups). Exists so event-triggered workflows — e.g. a TASK_CREATED
 * trigger whose payload only carries the invoice as an anchor entity — can
 * rejoin the matching pipeline without re-running extraction.
 */
export const getAPInvoiceStep = defineStepConfig({
  type: "get_ap_invoice",
  name: "Get AP Invoice",
  description:
    "Load an AP invoice by entity reference or id and expose its reference numbers for downstream matching",
  category: "Data Access",
  libraryModule: "AccountsPayable",

  inputSchema: z.object({
    apInvoice: entityOfType(SystemEntity.AP_INVOICE)
      .optional()
      .describe(
        "The AP invoice entity reference (e.g. a task trigger's anchorEntity). Must reference an AP invoice.",
      ),
    apInvoiceId: z
      .uuid()
      .optional()
      .describe(
        "Bare AP invoice id — alternative to `apInvoice`. Ignored when `apInvoice` is wired.",
      ),
  }),

  outputSchema: z.object({
    apInvoice: entityOfType(SystemEntity.AP_INVOICE).describe(
      "The loaded AP invoice entity reference",
    ),
    apInvoiceId: z.uuid().describe("The invoice's id (flat, for wiring)"),
    invoiceNumber: z
      .string()
      .optional()
      .describe("The supplier's invoice number, when present"),
    referenceIds: z
      .array(z.string())
      .describe(
        "PO / SO / load reference numbers stored on the invoice (empty when none)",
      ),
    status: z.string().describe("The invoice's current status"),
  }),

  configSchema: z.object({}),

  emits: [] as const,
});
